import { MAX_RECOMMENDED_PRODUCTS } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import {
  PublishedProductPicker,
  useInvalidateRecommendedProducts,
  useRecommendedProductsQuery,
} from '@/entities/product';
import {
  RecommendedProductRow,
  useAddRecommendedProduct,
  useMoveRecommendedProduct,
  useRemoveRecommendedProduct,
} from '@/features/product-recommendations';
import { Button, ContentCard, EmptyState } from '@/shared/ui';

/** HOME 추천 상품 관리 — 추가·제외·순서. 1번은 히어로 카드, 1~8번은 추천 상품 섹션에 나온다. */
export const LandingRecommendedManager = (): ReactElement => {
  const recommendedQuery = useRecommendedProductsQuery();
  const invalidate = useInvalidateRecommendedProducts();
  const addAction = useAddRecommendedProduct({ onSuccess: invalidate });
  const removeAction = useRemoveRecommendedProduct({ onSuccess: invalidate });
  const moveAction = useMoveRecommendedProduct({ onSuccess: invalidate });
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const products = recommendedQuery.data ?? [];
  const isFull = products.length >= MAX_RECOMMENDED_PRODUCTS;
  // 한 행이라도 바뀌는 중이면 모든 행의 버튼을 잠근다 — 순번이 같이 바뀌는 동안 꼬이지 않게.
  const isBusy = addAction.isPending || removeAction.isPending || moveAction.isPending;
  const errorMessage = removeAction.error?.message ?? moveAction.error?.message ?? null;

  // 제외·이동은 오류 한 줄(errorMessage)을 함께 쓴다. 새 요청을 보내기 전에 두 요청의 지난 오류를
  // 모두 지운다 — 아니면 다른 요청이 성공해도 옛 오류가 그 요청이 다시 돌 때까지 남는다.
  const clearRowErrors = (): void => {
    removeAction.reset();
    moveAction.reset();
  };

  const handleRemove = (productId: string): void => {
    clearRowErrors();
    removeAction.removeProduct(productId);
  };

  const handleMove = (productId: string, direction: 'UP' | 'DOWN'): void => {
    clearRowErrors();
    moveAction.moveProduct(productId, direction);
  };

  // 모달을 열고 닫을 때 지난 서버 거부 문구를 지운다 — 포트폴리오 구역과 같은 규칙.
  const changePicker = (next: boolean): void => {
    addAction.reset();
    setIsPickerOpen(next);
  };

  return (
    <ContentCard
      title="추천 상품"
      description={`1번은 HOME 히어로 카드에, 1~${MAX_RECOMMENDED_PRODUCTS}번은 추천 상품 섹션에 나옵니다. 빈 자리는 최신 공개 상품으로 채워집니다.`}
      actions={
        <>
          <span className="text-sm text-muted-foreground tabular-nums">
            {products.length}/{MAX_RECOMMENDED_PRODUCTS}
          </span>
          <Button size="sm" disabled={isFull || isBusy} onClick={() => changePicker(true)}>
            상품 추가
          </Button>
        </>
      }
    >
      {errorMessage !== null ? (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {errorMessage}
        </p>
      ) : null}

      {recommendedQuery.isPending ? (
        <EmptyState>불러오는 중…</EmptyState>
      ) : recommendedQuery.isError ? (
        <EmptyState isError>
          추천 상품을 불러오지 못했습니다.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => void recommendedQuery.refetch()}
          >
            다시 시도
          </button>
        </EmptyState>
      ) : products.length === 0 ? (
        <EmptyState>지정된 추천 상품이 없습니다. HOME 에는 최신 공개 상품이 나옵니다.</EmptyState>
      ) : (
        <ul className="flex flex-col gap-2">
          {products.map((product, index) => (
            <RecommendedProductRow
              key={product.id}
              product={product}
              order={index + 1}
              isFirst={index === 0}
              isLast={index === products.length - 1}
              isBusy={isBusy}
              onMove={(direction) => handleMove(product.id, direction)}
              onRemove={() => handleRemove(product.id)}
            />
          ))}
        </ul>
      )}

      {isPickerOpen ? (
        <PublishedProductPicker
          title="추천 상품 추가"
          description="공개된 상품만 추천할 수 있습니다."
          linkedLabel="추천 중"
          linkedProductIds={products.map((product) => product.id)}
          isAddDisabled={isFull}
          isPending={addAction.isPending}
          errorMessage={addAction.error?.message ?? null}
          onAdd={addAction.addProduct}
          onClose={() => changePicker(false)}
        />
      ) : null}
    </ContentCard>
  );
};
